export const getSummaryLineItemCount = ({
  sourceItemsLength = 0,
  processMetaItemsCount = 0,
  processMetaMatchedTotal = 0,
} = {}) => {
  const sourceCount = Number(sourceItemsLength || 0);
  if (sourceCount > 0) return sourceCount;
  const metaItems = Number(processMetaItemsCount || 0);
  if (metaItems > 0) return metaItems;
  const matchedTotal = Number(processMetaMatchedTotal || 0);
  if (matchedTotal > 0) return matchedTotal;
  return 0;
};

export const shouldFloatSummaryCard = ({
  viewportWidth = 0,
  lineItemCount = 0,
} = {}) => Number(viewportWidth || 0) >= 640 && Number(lineItemCount || 0) > 0;

export const computeFloatingSummaryMaxHeight = ({
  targetBottom = 0,
  cardTop = 0,
  bottomGap = 8,
  minHeight = 280,
} = {}) => {
  const availableHeight = Math.floor(Number(targetBottom || 0) - Number(cardTop || 0) - Number(bottomGap || 0));
  return Math.max(Number(minHeight || 0), availableHeight);
};

export const applyScrollableWheel = (element, deltaY) => {
  const el = element;
  const dy = Number(deltaY || 0);
  if (!el || !dy) return { consumed: false, nextScrollTop: 0 };
  if (el.scrollHeight <= el.clientHeight + 1) {
    return { consumed: false, nextScrollTop: el.scrollTop };
  }
  const atTop = el.scrollTop <= 0;
  const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
  const scrollingDown = dy > 0;
  const canConsume = (scrollingDown && !atBottom) || (!scrollingDown && !atTop);
  if (!canConsume) {
    return { consumed: false, nextScrollTop: el.scrollTop };
  }
  const nextScrollTop = el.scrollTop + dy;
  return { consumed: true, nextScrollTop };
};

export const normalizeProductCodeKey = (value) => (
  String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '')
);

export const sortMatchesByRerank = (rows) =>
  [...(rows || [])].sort((left, right) => {
    const leftAiRanked = Boolean(left?.ai_ranked);
    const rightAiRanked = Boolean(right?.ai_ranked);
    if (leftAiRanked !== rightAiRanked) return leftAiRanked ? -1 : 1;

    const leftAiRank = Number(left?.ai_rank);
    const rightAiRank = Number(right?.ai_rank);
    const leftAiRankFinite = Number.isFinite(leftAiRank);
    const rightAiRankFinite = Number.isFinite(rightAiRank);
    if (leftAiRankFinite && rightAiRankFinite && leftAiRank !== rightAiRank) return leftAiRank - rightAiRank;
    if (leftAiRankFinite !== rightAiRankFinite) return leftAiRankFinite ? -1 : 1;

    const leftRank = Number(left?.rank);
    const rightRank = Number(right?.rank);
    const leftRankFinite = Number.isFinite(leftRank);
    const rightRankFinite = Number.isFinite(rightRank);
    if (leftRankFinite && rightRankFinite && leftRank !== rightRank) return leftRank - rightRank;
    if (leftRankFinite !== rightRankFinite) return leftRankFinite ? -1 : 1;

    const leftScore = Number(left?.score);
    const rightScore = Number(right?.score);
    const leftScoreFinite = Number.isFinite(leftScore);
    const rightScoreFinite = Number.isFinite(rightScore);
    if (leftScoreFinite && rightScoreFinite && leftScore !== rightScore) return rightScore - leftScore;
    if (leftScoreFinite !== rightScoreFinite) return leftScoreFinite ? -1 : 1;
    return 0;
  });

export const reconcileSelectionMap = (sourceItems, sourceMatches, currentSelection = {}) => {
  const current = { ...(currentSelection || {}) };
  let changed = false;
  const grouped = {};
  (sourceMatches || []).forEach((entry) => {
    const key = String(entry.item_id ?? entry.itemId ?? '');
    if (!key) return;
    grouped[key] = grouped[key] || [];
    grouped[key].push(entry);
  });
  Object.keys(grouped).forEach((key) => {
    grouped[key] = sortMatchesByRerank(grouped[key]);
  });
  (sourceItems || []).forEach((item) => {
    const itemKey = String(item.id || '');
    if (!itemKey) return;
    const options = grouped[itemKey] || [];
    if (!options.length) return;

    const currentCode = String(current[itemKey] || '').trim();
    const topCode = String(options[0]?.product_code || '').trim();
    const currentCodeNorm = normalizeProductCodeKey(currentCode);
    const topCodeNorm = normalizeProductCodeKey(topCode);
    const currentMatch = options.find(
      (entry) => normalizeProductCodeKey(entry?.product_code) === currentCodeNorm
    );
    const currentIsManualOverride = Boolean(currentMatch?.manual_override);
    const hasAiRerank = options.some(
      (entry) => Boolean(entry?.ai_ranked) || Number.isFinite(Number(entry?.ai_rank))
    );
    const shouldUseTop =
      !currentCode
      || !currentMatch
      || (!currentIsManualOverride && hasAiRerank && currentCodeNorm !== topCodeNorm)
      || (!currentIsManualOverride && Boolean(options[0]?.ai_ranked) && !Boolean(currentMatch?.ai_ranked));

    if (shouldUseTop && topCode && currentCode !== topCode) {
      current[itemKey] = topCode;
      changed = true;
    }
  });
  return { next: current, changed };
};
